import { io, Socket } from 'socket.io-client';

type EventHandler = (...args: any[]) => void;

interface SubscriptionOptions {
  room?: string;
}

export type SocketStatus = 'disconnected' | 'connecting' | 'connected' | 'disabled';

interface SocketManagerConfig {
  url: string;
  auth: () => Record<string, string>;
}

class SocketManagerImpl {
  private socket: Socket | null = null;
  private config: SocketManagerConfig | null = null;
  private subscribers: Map<string, Set<EventHandler>> = new Map();
  private joinedRooms: Set<string> = new Set();
  private roomRefCounts: Map<string, number> = new Map();
  private namespaceSockets: Map<string, Socket> = new Map();
  private namespaceSubscribers: Map<string, Map<string, Set<EventHandler>>> = new Map();

  // Circuit breaker
  private connectionAttempts = 0;
  private disabled = false;
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly MAX_ATTEMPTS = 5;
  private readonly COOLDOWN_BASE_MS = 60_000;
  private readonly COOLDOWN_MAX_MS = 300_000;

  // Observable status
  private _status: SocketStatus = 'disconnected';
  private statusListeners: Set<(status: SocketStatus) => void> = new Set();

  get status() {
    return this._status;
  }

  private setStatus(s: typeof this._status) {
    this._status = s;
    this.statusListeners.forEach(fn => fn(s));
  }

  onStatusChange(fn: (status: SocketStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  init(config: SocketManagerConfig) {
    this.config = config;
    if (this.totalSubscribers() > 0 && !this.disabled && !this.socket) {
      this.connect();
    }
    if (this.namespaceSubscribers.size > 0) {
      this.namespaceSubscribers.forEach((_events, namespace) => {
        this.getNamespaceSocket(namespace);
      });
    }
  }

  subscribe(event: string, handler: EventHandler, opts?: SubscriptionOptions): () => void {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, new Set());
    }
    this.subscribers.get(event)!.add(handler);

    if (!this.socket && !this.disabled) {
      this.connect();
    }

    if (this.socket) {
      this.socket.on(event, handler);
    }

    if (opts?.room) {
      this.joinRoom(opts.room);
    }

    return () => {
      this.subscribers.get(event)?.delete(handler);
      if (this.subscribers.get(event)?.size === 0) {
        this.subscribers.delete(event);
      }
      this.socket?.off(event, handler);

      if (opts?.room) {
        this.releaseRoom(opts.room);
      }

      if (this.totalSubscribers() === 0) {
        this.disconnect();
      }
    };
  }

  emit(event: string, ...args: any[]) {
    if (this.socket?.connected) {
      this.socket.emit(event, ...args);
    }
  }

  joinRoom(room: string) {
    const count = this.roomRefCounts.get(room) || 0;
    this.roomRefCounts.set(room, count + 1);

    if (!this.joinedRooms.has(room) && this.socket?.connected) {
      this.socket.emit('join', room);
      this.socket.emit('join_room', room);
      this.joinedRooms.add(room);
    }
  }

  private releaseRoom(room: string) {
    const count = (this.roomRefCounts.get(room) || 1) - 1;
    if (count <= 0) {
      this.roomRefCounts.delete(room);
      if (this.joinedRooms.has(room) && this.socket?.connected) {
        this.socket.emit('leave', room);
        this.socket.emit('leave_room', room);
        this.joinedRooms.delete(room);
      }
      this.joinedRooms.delete(room);
    } else {
      this.roomRefCounts.set(room, count);
    }
  }

  destroy() {
    this.disconnect();
    this.namespaceSockets.forEach(nsSocket => {
      try {
        nsSocket.removeAllListeners();
        nsSocket.disconnect();
      } catch (_) {}
    });
    this.namespaceSockets.clear();
    this.namespaceSubscribers.clear();
    this.subscribers.clear();
    this.joinedRooms.clear();
    this.roomRefCounts.clear();
    this.config = null;
    this.disabled = false;
    this.connectionAttempts = 0;
    if (this.cooldownTimer) clearTimeout(this.cooldownTimer);
  }

  private connect() {
    if (!this.config || this.disabled || this.socket) return;
    this.setStatus('connecting');

    this.socket = io(this.config.url, {
      transports: ['websocket'],
      auth: this.config.auth(),
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      console.info('[SocketManager] Connected');
      this.setStatus('connected');
      this.connectionAttempts = 0;

      this.subscribers.forEach((handlers, event) => {
        handlers.forEach(h => {
          this.socket!.off(event, h);
          this.socket!.on(event, h);
        });
      });

      this.joinedRooms.forEach(room => {
        this.socket!.emit('join', room);
        this.socket!.emit('join_room', room);
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.info(`[SocketManager] Disconnected: ${reason}`);
      this.setStatus('disconnected');
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (err) => {
      this.connectionAttempts++;
      console.warn(
        `[SocketManager] Connection error (${this.connectionAttempts}/${this.MAX_ATTEMPTS}): ${err.message}`
      );
      if (this.connectionAttempts >= this.MAX_ATTEMPTS) {
        this.activateCircuitBreaker();
      }
    });
  }

  private activateCircuitBreaker() {
    this.disconnect();
    this.disabled = true;
    this.setStatus('disabled');

    const delay = Math.min(
      this.COOLDOWN_MAX_MS,
      this.COOLDOWN_BASE_MS * Math.pow(2, Math.floor(this.connectionAttempts / this.MAX_ATTEMPTS) - 1)
    );

    console.warn(`[SocketManager] Circuit breaker active. Retry in ${delay / 1000}s`);

    this.cooldownTimer = setTimeout(() => {
      this.disabled = false;
      this.connectionAttempts = 0;
      if (this.totalSubscribers() > 0) {
        this.connect();
      } else {
        this.setStatus('disconnected');
      }
    }, delay);
  }

  private disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.namespaceSockets.forEach(nsSocket => {
      try {
        nsSocket.removeAllListeners();
        nsSocket.disconnect();
      } catch (_) {}
    });
    this.namespaceSockets.clear();
    this.setStatus('disconnected');
  }

  private totalSubscribers(): number {
    let total = 0;
    this.subscribers.forEach(set => (total += set.size));
    return total;
  }

  private getNamespaceSocket(namespace: string): Socket | null {
    if (!this.socket || !this.config) {
      if (!this.disabled) this.connect();
      if (!this.socket || !this.config) return null;
    }

    if (this.namespaceSockets.has(namespace)) {
      return this.namespaceSockets.get(namespace) || null;
    }

    const manager = (this.socket as any).io;
    if (!manager) return null;
    const nsSocket = manager.socket(namespace, {
      auth: this.config.auth(),
      transports: ['websocket'],
    });
    this.namespaceSockets.set(namespace, nsSocket);
    this.attachNamespaceHandlers(namespace, nsSocket);
    return nsSocket;
  }

  subscribeNamespace(namespace: string, event: string, handler: EventHandler): () => void {
    if (!this.namespaceSubscribers.has(namespace)) {
      this.namespaceSubscribers.set(namespace, new Map());
    }
    const nsEvents = this.namespaceSubscribers.get(namespace)!;
    if (!nsEvents.has(event)) {
      nsEvents.set(event, new Set());
    }
    nsEvents.get(event)!.add(handler);

    const nsSocket = this.getNamespaceSocket(namespace);
    if (!nsSocket) {
      return () => {
        nsEvents.get(event)?.delete(handler);
        if (nsEvents.get(event)?.size === 0) {
          nsEvents.delete(event);
        }
      };
    }
    nsSocket.on(event, handler);
    return () => {
      try {
        nsSocket.off(event, handler);
      } catch (_) {}
      nsEvents.get(event)?.delete(handler);
      if (nsEvents.get(event)?.size === 0) {
        nsEvents.delete(event);
      }
    };
  }

  emitNamespace(namespace: string, event: string, ...args: any[]) {
    const nsSocket = this.getNamespaceSocket(namespace);
    if (nsSocket?.connected) {
      nsSocket.emit(event, ...args);
    }
  }

  private attachNamespaceHandlers(namespace: string, nsSocket: Socket) {
    const nsEvents = this.namespaceSubscribers.get(namespace);
    if (!nsEvents) return;
    nsEvents.forEach((handlers, event) => {
      handlers.forEach(handler => {
        nsSocket.off(event, handler);
        nsSocket.on(event, handler);
      });
    });
  }
}

export const SocketManager = new SocketManagerImpl();
